/**
 * The ranked list's shared geometry and small rules: one grid per number of
 * metric columns (literal classes, so Tailwind sees them; metric columns hide
 * on narrow screens), the score bar's length against the best and worst
 * scores shown, and the filters.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenRankedText } from '@trafficpulse/shared';

const GRIDS = [
  'grid-cols-[2.75rem_minmax(0,1fr)_7rem_3.75rem]',
  'grid-cols-[2.75rem_minmax(0,1fr)_7rem_3.75rem] sm:grid-cols-[2.75rem_minmax(0,1fr)_5.25rem_7rem_3.75rem]',
  'grid-cols-[2.75rem_minmax(0,1fr)_7rem_3.75rem] sm:grid-cols-[2.75rem_minmax(0,1fr)_repeat(2,5.25rem)_7rem_3.75rem]',
  'grid-cols-[2.75rem_minmax(0,1fr)_7rem_3.75rem] sm:grid-cols-[2.75rem_minmax(0,1fr)_repeat(3,5.25rem)_7rem_3.75rem]',
  'grid-cols-[2.75rem_minmax(0,1fr)_7rem_3.75rem] sm:grid-cols-[2.75rem_minmax(0,1fr)_repeat(4,5.25rem)_7rem_3.75rem]',
];

export const rowGrid = (columns: number): string => `grid items-center gap-x-3 ${GRIDS[Math.min(columns, GRIDS.length - 1)]}`;

/** Bar length 0–100 for a score, between the lowest (or 0) and the best score shown. */
export function barScale(scores: Array<number | null>): (score: number | null) => number {
  const ranked = scores.filter((s): s is number => typeof s === 'number');
  const max = Math.max(0, ...ranked);
  const min = Math.min(0, ...ranked);
  return (score) => (score === null || max === min ? 0 : Math.round(((score - min) / (max - min)) * 100));
}

export type TextFilter = 'pool' | 'reusable' | 'all';

const KEEP: Record<TextFilter, (t: DropGenRankedText) => boolean> = {
  pool: (t) => t.inPool,
  reusable: (t) => t.reusable || t.excluded,
  all: () => true,
};

/** The rows a filter and a search keep — the search reads the text, its prompt group and its id. */
export function filterTexts(texts: DropGenRankedText[], filter: TextFilter, search: string): DropGenRankedText[] {
  const q = search.trim().toLowerCase();
  return texts
    .filter(KEEP[filter])
    .filter((t) => !q || [t.text, t.groupName, t.templateId].some((s) => s?.toLowerCase().includes(q)));
}
